"use client";

import { useState } from "react";
import {
  Checkbox,
  CopyButton,
  DataTable,
  Notice,
  TextAreaField,
} from "@/components/tool-kit/primitives";
import {
  CARD,
  HINT,
  LABEL,
  WIDGET_H2,
  WIDGET_H3,
} from "@/components/tool-kit/styles";
import { useToolUi } from "@/components/tools/tool-ui";
import { t } from "@/lib/i18n/interpolate";
import {
  type KoreanOptions,
  RR_FINALS,
  RR_INITIALS,
  RR_VOWELS,
  romanizeKorean,
  TRANSLIT_INITIALS,
} from "@/lib/tool-kit/converters/korean";

/**
 * McCune–Reischauer letter values as the Library of Congress table gives them (Korean
 * Romanization Table, 2009, Appendix 7, and the examples in its rules): shown for
 * reference only, because ALA-LC's position rules depend on dictionary pronunciation.
 */
const MR_CONSONANTS: Record<string, string> = {
  ㄱ: "k / g",
  ㄲ: "kk",
  ㄴ: "n",
  ㄷ: "t / d",
  ㄸ: "tt",
  ㄹ: "r / l",
  ㅁ: "m",
  ㅂ: "p / b",
  ㅃ: "pp",
  ㅅ: "s, sh",
  ㅆ: "ss",
  ㅇ: "— / ng",
  ㅈ: "ch / j",
  ㅉ: "tch",
  ㅊ: "ch’",
  ㅋ: "k’",
  ㅌ: "t’",
  ㅍ: "p’",
  ㅎ: "h",
};

const MR_VOWELS: Record<string, string> = {
  ㅏ: "a",
  ㅐ: "ae",
  ㅑ: "ya",
  ㅒ: "yae",
  ㅓ: "ŏ",
  ㅔ: "e",
  ㅕ: "yŏ",
  ㅖ: "ye",
  ㅗ: "o",
  ㅘ: "wa",
  ㅙ: "wae",
  ㅚ: "oe",
  ㅛ: "yo",
  ㅜ: "u",
  ㅝ: "wŏ",
  ㅞ: "we",
  ㅟ: "wi",
  ㅠ: "yu",
  ㅡ: "ŭ",
  ㅢ: "ŭi",
  ㅣ: "i",
};

/** Where each consonant's final sound lands once neutralized, for the letter table. */
const FINAL_SOUND: Record<string, string> = {
  ㄱ: "ㄱ",
  ㄲ: "ㄱ",
  ㅋ: "ㄱ",
  ㄴ: "ㄴ",
  ㄷ: "ㄷ",
  ㅅ: "ㄷ",
  ㅆ: "ㄷ",
  ㅈ: "ㄷ",
  ㅊ: "ㄷ",
  ㅌ: "ㄷ",
  ㅎ: "ㄷ",
  ㄹ: "ㄹ",
  ㅁ: "ㅁ",
  ㅂ: "ㅂ",
  ㅍ: "ㅂ",
  ㅇ: "ㅇ",
};

/** English strings. A translated content file overrides these through `ui`. */
export const KOREAN_ROMANIZER_STRINGS = {
  inputLabel: "Hangul text",
  inputHint:
    "Words, names or whole sentences. Hanja and Latin letters pass through unchanged.",
  sample: "서울 종로 독립문, 대관령",
  options: "Options for Revised Romanization",
  personalName:
    "Personal name: family name first, no sound changes in the given name (한복남 → Han Boknam)",
  capitalize: "Capitalize each word (names and places)",
  nounH:
    "Write ㅎ after ㄱ, ㄷ, ㅂ separately, the rule for nouns (묵호 → Mukho)",
  hyphenate: "Add a hyphen where syllables could be misread (중앙 → Jung-ang)",
  resultsHeading: "Romanized",
  rrTitle: "Revised Romanization",
  rrUse:
    "South Korea's official system since 2000: road signs, maps, passports.",
  translitTitle: "Transliteration (letter by letter)",
  translitUse:
    "Revised Romanization's reversible form for academic work — spelling, not sound.",
  mrNote:
    "McCune–Reischauer, the system the Library of Congress uses (ALA-LC), is not generated here: library romanization follows dictionary pronunciations and word-division rules that can't be read from the spelling alone. The letter table gives its basic values for reference.",
  changesHeading: "Sound changes applied",
  changeLine: "{syllables}: {from} → {to} ({change})",
  changes: {
    liaison: "the final consonant moves to the next syllable",
    nasalization: "nasalization",
    liquidization: "liquidization",
    aspiration: "aspiration",
    palatalization: "palatalization",
    "h-dropped": "ㅎ is silent before a vowel",
  },
  noChanges: "No sound changes between syllables in this text.",
  enterText: "Type or paste some Hangul to romanize.",
  copy: "Copy",
  copied: "Copied!",
  tableHeading: "Letter values",
  letterColumn: "Letter",
  rrColumn: "Revised Romanization",
  translitColumn: "Transliteration",
  mrColumn: "McCune–Reischauer (ALA-LC)",
  rrConsonantValue: "{initial} / {final}",
  tableNote:
    "Revised Romanization: the first value starts a syllable, the second ends one. McCune–Reischauer values depend on position: k / g means k at the start of a word and g between voiced sounds; ㅇ is silent at the start of a syllable and ng at the end; ㅅ is sh before ㅟ; ’ marks an aspirated consonant.",
} as const;

type Strings = typeof KOREAN_ROMANIZER_STRINGS;

const CONSONANTS = Object.keys(RR_INITIALS);
const VOWEL_LETTERS = Object.keys(RR_VOWELS);

function letterRows(strings: Strings): string[][] {
  const consonants = CONSONANTS.map((jamo) => {
    const final = RR_FINALS[FINAL_SOUND[jamo] ?? ""];
    const initial = RR_INITIALS[jamo] || "—";
    return [
      jamo,
      final ? t(strings.rrConsonantValue, { initial, final }) : initial,
      TRANSLIT_INITIALS[jamo] || "—",
      MR_CONSONANTS[jamo],
    ];
  });
  const vowels = VOWEL_LETTERS.map((jamo) => [
    jamo,
    RR_VOWELS[jamo],
    RR_VOWELS[jamo],
    MR_VOWELS[jamo],
  ]);
  return [...consonants, ...vowels];
}

function ResultCard({
  title,
  use,
  text,
  strings,
}: {
  title: string;
  use: string;
  text: string;
  strings: Strings;
}) {
  return (
    <div className={`${CARD} space-y-2`}>
      <h3 className={WIDGET_H3}>{title}</h3>
      <p className={HINT}>{use}</p>
      <p className="whitespace-pre-wrap break-words text-foreground text-lg leading-relaxed">
        {text}
      </p>
      <CopyButton
        copiedLabel={strings.copied}
        label={strings.copy}
        small
        text={text}
      />
    </div>
  );
}

export default function KoreanRomanizerWidget() {
  const strings = useToolUi(KOREAN_ROMANIZER_STRINGS);
  const [text, setText] = useState<string>(strings.sample);
  const [options, setOptions] = useState<KoreanOptions>({
    nounH: true,
    hyphenate: false,
    capitalize: true,
    personalName: false,
  });

  const setOption = (key: keyof KoreanOptions) => (value: boolean) =>
    setOptions((current) => ({ ...current, [key]: value }));

  const rr = romanizeKorean(text, "rr", options);
  const translit = romanizeKorean(text, "translit", options);
  const empty = text.trim() === "";

  return (
    <div className="space-y-6">
      <TextAreaField
        hint={strings.inputHint}
        id="ko-input"
        label={strings.inputLabel}
        onChange={setText}
        prose
        rows={4}
        value={text}
      />
      <fieldset className="space-y-2">
        <legend className={LABEL}>{strings.options}</legend>
        <Checkbox
          checked={options.personalName}
          label={strings.personalName}
          onChange={setOption("personalName")}
        />
        <Checkbox
          checked={options.capitalize}
          label={strings.capitalize}
          onChange={setOption("capitalize")}
        />
        <Checkbox
          checked={options.nounH}
          label={strings.nounH}
          onChange={setOption("nounH")}
        />
        <Checkbox
          checked={options.hyphenate}
          label={strings.hyphenate}
          onChange={setOption("hyphenate")}
        />
      </fieldset>
      {empty ? (
        <Notice>{strings.enterText}</Notice>
      ) : (
        <section aria-live="polite" className="space-y-3">
          <h2 className={WIDGET_H2}>{strings.resultsHeading}</h2>
          <div className="grid gap-4 sm:grid-cols-2">
            <ResultCard
              strings={strings}
              text={rr.text}
              title={strings.rrTitle}
              use={strings.rrUse}
            />
            <ResultCard
              strings={strings}
              text={translit.text}
              title={strings.translitTitle}
              use={strings.translitUse}
            />
          </div>
          <div className="space-y-1">
            <p className={LABEL}>{strings.changesHeading}</p>
            {rr.changes.length === 0 ? (
              <p className={HINT}>{strings.noChanges}</p>
            ) : (
              <ul className="space-y-1 text-foreground text-sm">
                {rr.changes.map((change, index) => (
                  // The same change can occur twice in one text, so position is part of the key.
                  // biome-ignore lint/suspicious/noArrayIndexKey: positional list
                  <li key={index}>
                    {t(strings.changeLine, {
                      syllables: change.syllables,
                      from: change.from,
                      to: change.to,
                      change: strings.changes[change.change],
                    })}
                  </li>
                ))}
              </ul>
            )}
          </div>
        </section>
      )}
      <Notice>{strings.mrNote}</Notice>
      <section className={`${CARD} space-y-3`}>
        <h2 className={WIDGET_H2}>{strings.tableHeading}</h2>
        <DataTable
          head={[
            strings.letterColumn,
            strings.rrColumn,
            strings.translitColumn,
            strings.mrColumn,
          ]}
          rows={letterRows(strings)}
        />
        <p className={HINT}>{strings.tableNote}</p>
      </section>
    </div>
  );
}
